import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X, UserPlus, UserMinus } from "lucide-react";
import { cn } from "@/lib/utils";

const fmt$ = (v) => `${v < 0 ? "-" : "+"}$${Math.abs(Math.round(v)).toLocaleString("en-US")}`;
const money = (v) => `$${Math.round(v).toLocaleString("en-US")}`;

const DEFAULT_BONUS = { enabled: false, pct: 5, quarters: [{ on: true, amount: null }, { on: true, amount: null }, { on: true, amount: null }, { on: true, amount: null }] };
const Q_LABELS = ["Q1 · pays Jul", "Q2 · pays Oct", "Q3 · pays Jan", "Q4 · pays Apr"];

export function SalaryAdjustmentsDialog({ open, onClose, settings, onChange, onBonusChange, bonusBase = 0, bonusAutoQuarter = 0, monthOptions = [] }) {
  const [mode, setMode] = useState("remove");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [label, setLabel] = useState("");
  const [start, setStart] = useState("");

  if (!settings) return null;
  const categories = (settings.overhead_items || []).map((i) => i.name);
  const adjustments = settings.salary_adjustments || [];
  const bonus = { ...DEFAULT_BONUS, ...(settings.bonus || {}) };
  const quarters = (bonus.quarters?.length === 4 ? bonus.quarters : DEFAULT_BONUS.quarters).map((q) => ({ ...q }));

  const patchBonus = (p) => onBonusChange({ ...bonus, quarters, ...p });
  const patchQuarter = (i, p) => {
    const next = quarters.map((q, idx) => (idx === i ? { ...q, ...p } : q));
    onBonusChange({ ...bonus, quarters: next });
  };

  const addAdjustment = () => {
    const annual = parseFloat(amount);
    if (!annual || !category) return;
    const signed = mode === "remove" ? -Math.abs(annual) : Math.abs(annual);
    onChange([
      ...adjustments,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label: label.trim(), category, annual: signed, start: start || null },
    ]);
    setAmount("");
    setLabel("");
    setStart("");
  };

  const monthLabel = (key) => monthOptions.find((m) => m.key === key)?.label || key;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl" data-testid="salary-adjustments-modal">
        <DialogHeader>
          <DialogTitle>Salary Adjustments</DialogTitle>
          <p className="text-xs text-slate-500">
            Add or remove a salary and the matching cost line adjusts by annual ÷ 12 every month from the chosen start month forward. Click "Save Scenario" to persist.
          </p>
        </DialogHeader>

        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button onClick={() => setMode("remove")} data-testid="adj-mode-remove"
              className={cn("px-2.5 h-8 text-xs font-medium flex items-center gap-1", mode === "remove" ? "bg-red-600 text-white" : "bg-white text-slate-600")}>
              <UserMinus className="h-3 w-3" /> Remove
            </button>
            <button onClick={() => setMode("add")} data-testid="adj-mode-add"
              className={cn("px-2.5 h-8 text-xs font-medium flex items-center gap-1", mode === "add" ? "bg-emerald-600 text-white" : "bg-white text-slate-600")}>
              <UserPlus className="h-3 w-3" /> Add
            </button>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Annual Salary $</div>
            <Input type="number" step="5000" placeholder="120000" value={amount}
              onChange={(e) => setAmount(e.target.value)} className="h-8 w-28 text-xs" data-testid="adj-amount" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Section</div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="adj-category"
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] min-w-[170px]">
              <option value="">Select section…</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Start Month</div>
            <select value={start} onChange={(e) => setStart(e.target.value)} data-testid="adj-start-month"
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] min-w-[110px]">
              <option value="">Current month</option>
              {monthOptions.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Note (optional)</div>
            <Input placeholder="e.g. New PM hire" value={label} onChange={(e) => setLabel(e.target.value)}
              className="h-8 text-xs" data-testid="adj-label" />
          </div>
          <Button size="sm" className="h-8 text-xs"
            disabled={!parseFloat(amount) || !category} onClick={addAdjustment} data-testid="adj-add-btn">
            Apply
          </Button>
        </div>
        {amount && category && (
          <p className="text-[11px] text-slate-500 -mt-1" data-testid="adj-preview">
            {mode === "remove" ? "Removes" : "Adds"} <b>${Math.abs(parseFloat(amount) / 12).toLocaleString("en-US", { maximumFractionDigits: 0 })}/month</b> {mode === "remove" ? "from" : "to"} <b>{category}</b> starting <b>{start ? monthLabel(start) : "this month"}</b>
          </p>
        )}

        <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-auto">
          {adjustments.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4" data-testid="adj-empty">No salary adjustments yet</p>
          ) : adjustments.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`adj-row-${a.id}`}>
              <span className={cn("text-xs font-bold tabular-nums w-24", a.annual < 0 ? "text-red-600" : "text-emerald-600")}>
                {fmt$(a.annual)}/yr
              </span>
              <span className="text-xs text-slate-700 font-medium">{a.category}</span>
              <span className={cn("text-[11px] tabular-nums", a.annual < 0 ? "text-red-500" : "text-emerald-600")}>
                {fmt$(a.annual / 12)}/mo
              </span>
              <span className="text-[10px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`adj-start-${a.id}`}>
                from {a.start ? monthLabel(a.start) : "current"}
              </span>
              {a.label && <span className="text-[11px] text-slate-400 italic truncate">{a.label}</span>}
              <button onClick={() => onChange(adjustments.filter((x) => x.id !== a.id))}
                className="ml-auto text-slate-400 hover:text-red-600" data-testid={`adj-delete-${a.id}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Company Bonus */}
        <div className="border-t border-slate-200 pt-3" data-testid="bonus-section">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
              <input type="checkbox" checked={!!bonus.enabled} onChange={(e) => patchBonus({ enabled: e.target.checked })}
                className="h-4 w-4 accent-[#007BFF]" data-testid="bonus-enabled" />
              Company Bonus
            </label>
            <div className="flex items-center gap-1">
              <input type="number" step="0.5" key={bonus.pct} defaultValue={bonus.pct}
                onBlur={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== bonus.pct) patchBonus({ pct: v }); }}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                className="w-14 h-7 text-xs text-right border border-slate-200 rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                data-testid="bonus-pct" />
              <span className="text-[11px] text-slate-500">% of overhead</span>
            </div>
            <span className="text-[11px] text-slate-400" data-testid="bonus-math">
              {bonus.pct}% of {money(bonusBase)} (rolling-12 overhead) ÷ 4 = <b>{money(bonusAutoQuarter)}/quarter</b>
            </span>
          </div>
          {bonus.enabled && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              {quarters.map((q, i) => (
                <div key={i} className={`border rounded-md p-2 ${q.on ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`} data-testid={`bonus-q-${i}`}>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!q.on} onChange={(e) => patchQuarter(i, { on: e.target.checked })}
                      className="h-3.5 w-3.5 accent-[#007BFF]" data-testid={`bonus-q-on-${i}`} />
                    {Q_LABELS[i]}
                  </label>
                  <input type="number" step="5000"
                    key={`${i}-${q.amount ?? ""}`}
                    defaultValue={q.amount ?? ""}
                    placeholder={Math.round(bonusAutoQuarter).toLocaleString("en-US")}
                    disabled={!q.on}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const v = raw === "" ? null : parseFloat(raw);
                      if (raw === "" || !Number.isNaN(v)) patchQuarter(i, { amount: v });
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    className="mt-1.5 w-full h-7 text-[11px] text-right border border-slate-200 rounded px-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#007BFF] tabular-nums disabled:bg-slate-100"
                    data-testid={`bonus-q-amount-${i}`} />
                </div>
              ))}
            </div>
          )}
          {bonus.enabled && (
            <p className="text-[10px] text-slate-400 mt-1.5">Blank quarter amount = auto ({money(bonusAutoQuarter)}). Amounts add to Overhead Costs in the paying month. Save Scenario to persist.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
